import { useEffect, type ReactNode } from "react";
import type { CategoryKind } from "../api/types";
import { formatCents } from "../lib/money";

/**
 * Maps a bucket kind to its themed colour variable.
 *
 * The three built-in kinds always use the active theme's variable, even though
 * the server stores a hex colour on every category (a leftover from before
 * theming existed): if the stored value won, Spend and Save would stay the
 * launch palette's orange and teal under every theme a family picks. `explicit`
 * is honoured only for `custom` categories, where the stored colour is the only
 * source of truth — a parent-created category has no themed variable to fall
 * back on.
 */
export function kindColor(kind: CategoryKind, explicit?: string): string {
  switch (kind) {
    case "spend":
      return "var(--c-spend)";
    case "save":
      return "var(--c-save)";
    case "give":
      return "var(--c-give)";
    default:
      return explicit || "var(--c-custom)";
  }
}

/** A signed money amount, coloured by direction. */
export function Amount({
  cents,
  showSign = false,
  className = "",
}: {
  cents: number;
  showSign?: boolean;
  className?: string;
}) {
  const tone =
    cents > 0 ? "amount--positive" : cents < 0 ? "amount--negative" : "";
  const text = formatCents(cents);
  return (
    <span className={`amount ${tone} ${className}`}>
      {showSign && cents > 0 ? "+" : ""}
      {text}
    </span>
  );
}

export function ErrorMessage({ children }: { children: ReactNode }) {
  if (!children) return null;
  return (
    <p className="alert" role="alert">
      {children}
    </p>
  );
}

/** A labelled form field. */
export function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: ReactNode;
}) {
  return (
    <div className="field">
      <label>{label}</label>
      {children}
      {hint ? <span className="field__hint">{hint}</span> : null}
    </div>
  );
}

/**
 * A modal dialog. Escape closes it and focus is trapped loosely by rendering
 * over a backdrop; a click on the backdrop dismisses.
 */
export function Modal({
  title,
  onClose,
  children,
}: {
  title: string;
  onClose: () => void;
  children: ReactNode;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div
      className="modal-backdrop"
      onClick={onClose}
      role="presentation"
    >
      <div
        className="modal"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="modal__title">{title}</h2>
        {children}
      </div>
    </div>
  );
}

/** A two-option segmented control, used for the deposit/withdrawal switch. */
export function Segmented<T extends string>({
  value,
  options,
  onChange,
}: {
  value: T;
  options: { value: T; label: string }[];
  onChange: (value: T) => void;
}) {
  return (
    <div className="segmented" role="group">
      {options.map((opt) => (
        <button
          key={opt.value}
          type="button"
          className={`segmented__option ${
            opt.value === value ? "segmented__option--active" : ""
          }`}
          aria-pressed={opt.value === value}
          onClick={() => onChange(opt.value)}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}
